import type {
  ISubaccountMapping,
  ISubaccountMappingList,
  ISubaccountMappingQuery,
  ISubaccountMappingResponse,
  ISubaccountMappingWriteRequest,
} from "@pulse-ui/codegen-v2/generated_ts/subaccount_mapping";
import { api } from "../base";

export type {
  ISubaccountMapping,
  ISubaccountMappingList,
  ISubaccountMappingQuery,
  ISubaccountMappingResponse,
  ISubaccountMappingWriteRequest,
};
export { SubaccountProductType } from "@pulse-ui/codegen-v2/generated_ts/subaccount_mapping";

/** Identifies one mapping; both fields are required by the DELETE endpoint. */
export type SubaccountMappingKey = Pick<ISubaccountMapping, "company_code" | "product_code">;

const SUBACCOUNT_MAPPINGS_URL = "/api/trading-ops/v1/subaccount-mappings";
const SUBACCOUNT_MAPPING_RESOLVE_URL = "/api/trading-ops/v1/subaccount-mapping";

const subaccountMappingsEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Every mapping, sorted by (company_code, product_code). */
    getSubaccountMappings: build.query<ISubaccountMappingList, void>({
      query: () => ({ url: SUBACCOUNT_MAPPINGS_URL }),
      providesTags: ["SubaccountMappings"],
    }),

    /** The subaccount mapped to (account's company_code, product_type); 503 when unconfigured. */
    resolveSubaccountMapping: build.query<ISubaccountMappingResponse, ISubaccountMappingQuery>({
      query: ({ account_id, product_type }) => ({
        url: SUBACCOUNT_MAPPING_RESOLVE_URL,
        params: { account_id, product_type },
      }),
      providesTags: ["SubaccountMappings"],
    }),

    /** Creates a mapping; 409 when the key already exists in any casing. */
    createSubaccountMapping: build.mutation<ISubaccountMapping, ISubaccountMappingWriteRequest>({
      query: (body) => ({ url: SUBACCOUNT_MAPPINGS_URL, method: "POST", body }),
      invalidatesTags: ["SubaccountMappings"],
    }),

    /** Replaces the subaccount on an existing key; 404 when absent. */
    updateSubaccountMapping: build.mutation<ISubaccountMapping, ISubaccountMappingWriteRequest>({
      query: (body) => ({ url: SUBACCOUNT_MAPPINGS_URL, method: "PUT", body }),
      invalidatesTags: ["SubaccountMappings"],
    }),

    /** Deletes the mapping for a key; 404 when absent. */
    deleteSubaccountMapping: build.mutation<ISubaccountMapping, SubaccountMappingKey>({
      query: ({ company_code, product_code }) => ({
        url: SUBACCOUNT_MAPPINGS_URL,
        method: "DELETE",
        params: { company_code, product_code },
      }),
      invalidatesTags: ["SubaccountMappings"],
    }),
  }),
});

export const {
  useGetSubaccountMappingsQuery,
  useResolveSubaccountMappingQuery,
  useCreateSubaccountMappingMutation,
  useUpdateSubaccountMappingMutation,
  useDeleteSubaccountMappingMutation,
} = subaccountMappingsEndpoints;
